import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Moon, Sun, AlertCircle } from 'lucide-react';

export const Login = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(''); // Reset error message
    
    if (!email || !password) {
      setErrorMessage('Compila tutti i campi');
      toast.error('Compila tutti i campi');
      return;
    }

    try {
      await login(email, password);
      toast.success('Bentornato!');
      navigate('/dashboard');
    } catch (err) {
      // Set error message for display in the form
      setErrorMessage(err.message);
      // Also show toast
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between h-14 px-4 border-b">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          data-testid="login-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 
          className="text-xl font-bold tracking-tight text-primary" 
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          FANTACLASSE
        </h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="login-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Bentornato!
            </CardTitle>
            <CardDescription>
              Accedi per continuare a giocare
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Error Alert */}
            {errorMessage && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {/* Email Login */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="la.tua@email.it"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrorMessage(''); // Clear error on input change
                  }}
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="La tua password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrorMessage(''); // Clear error on input change
                  }}
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Accesso...
                  </>
                ) : (
                  'Accedi'
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              Non hai un account?{' '}
              <Link
                to="/register"
                className="text-primary font-medium hover:underline"
                data-testid="login-register-link"
              >
                Registrati
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};
